// src/pages/teacher/TeacherDashboard.jsx
import { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Stack, CircularProgress,
  Alert, Grid, LinearProgress, Chip,
} from '@mui/material';
import SchoolIcon            from '@mui/icons-material/School';
import PeopleIcon            from '@mui/icons-material/People';
import PlayCircleIcon        from '@mui/icons-material/PlayCircle';
import GradeIcon             from '@mui/icons-material/Grade';
import TrendingUpIcon        from '@mui/icons-material/TrendingUp';
import { useTranslation }    from 'react-i18next';
import { useNavigate }       from 'react-router-dom';
import { getClassStats }     from '../../api/teacherApi';
import { useOrg }            from '../../context/OrgContext';

function StatCard({ label, value, color, icon, onClick }) {
  return (
    <Paper variant="outlined"
      onClick={onClick}
      sx={{ p: 2, borderRadius: 2, textAlign: 'center', flex: 1,
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? { borderColor: color, bgcolor: color + '11' } : {},
        transition: 'all .15s' }}>
      <Box sx={{ color, mb: 0.5 }}>{icon}</Box>
      <Typography fontSize={26} fontWeight={700} color={color}>{value}</Typography>
      <Typography fontSize={12} color="text.secondary">{label}</Typography>
    </Paper>
  );
}

export default function TeacherDashboard() {
  const { t }       = useTranslation('teacher');
  const navigate    = useNavigate();
  const { orgName } = useOrg();
  const [stats,   setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  useEffect(() => {
    getClassStats(30)
      .then(r => setStats(r.data))
      .catch(e => setError(e?.response?.data?.detail?.message || t('errorLoad')))
      .finally(() => setLoading(false));
  }, [t]);

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', px: 3, py: 4 }}>
      <Stack direction="row" alignItems="center" gap={2} mb={3}>
        <SchoolIcon sx={{ fontSize: 32, color: '#4f46e5' }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>{t('dashboard')}</Typography>
          {orgName && (
            <Typography fontSize={13} color="text.secondary">{orgName}</Typography>
          )}
        </Box>
      </Stack>

      {error  && <Alert severity="error"  sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb: 3, borderRadius: 1 }} />}

      {stats && (
        <>
          <Stack direction="row" spacing={2} mb={3} flexWrap="wrap">
            <StatCard label={t('totalStudents')} value={stats.total_students}
              color="#4f46e5" icon={<PeopleIcon />}
              onClick={() => navigate('/teacher/students')} />
            <StatCard label={t('activeNow')} value={stats.active_now}
              color="#0f766e" icon={<PlayCircleIcon />}
              onClick={() => navigate('/teacher/live')} />
            <StatCard label={t('totalExams')} value={stats.total_exams}
              color="#b45309" icon={<GradeIcon />}
              onClick={() => navigate('/teacher/grading')} />
            <StatCard label={t('passRate')} value={stats.pass_rate + '%'}
              color={stats.pass_rate >= 70 ? '#16a34a' : '#dc2626'}
              icon={<TrendingUpIcon />}
              onClick={() => navigate('/teacher/grading')} />
          </Stack>

          {/* Score moyen */}
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
              <Typography fontSize={14} fontWeight={600}>{t('avgScore')}</Typography>
              <Typography fontSize={22} fontWeight={700}
                color={stats.avg_score >= 70 ? '#16a34a' : '#dc2626'}>
                {stats.avg_score}%
              </Typography>
            </Stack>
            <LinearProgress variant="determinate" value={Math.min(stats.avg_score, 100)}
              sx={{ height: 8, borderRadius: 4,
                bgcolor: stats.avg_score >= 70 ? '#bbf7d0' : '#fecaca',
                '& .MuiLinearProgress-bar': {
                  bgcolor: stats.avg_score >= 70 ? '#16a34a' : '#ef4444' } }} />
            <Typography fontSize={11} color="text.secondary" mt={0.5}>
              {stats.passed_exams}/{stats.total_exams} {t('examsPassed')}
            </Typography>
          </Paper>

          {/* Top labs */}
          {stats.top_labs?.length > 0 && (
            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{ px: 2, py: 1.5, bgcolor: '#f8fafc', borderBottom: '1px solid',
                borderColor: 'divider' }}>
                <Typography fontSize={13} fontWeight={600}>{t('topLabs')}</Typography>
              </Box>
              <Box sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  {stats.top_labs.map((lab, i) => (
                    <Stack key={i} direction="row" alignItems="center" spacing={2}>
                      <Box sx={{ width: 24, height: 24, borderRadius: '50%',
                        bgcolor: '#eef2ff', display: 'flex', alignItems: 'center',
                        justifyContent: 'center' }}>
                        <Typography fontSize={11} fontWeight={700} color="#4f46e5">
                          {i + 1}
                        </Typography>
                      </Box>
                      <Typography fontSize={13} flex={1}>{lab.name}</Typography>
                      <Chip label={lab.count + ' sessions'} size="small"
                        sx={{ fontSize: 11, bgcolor: '#eef2ff', color: '#4f46e5' }} />
                    </Stack>
                  ))}
                </Stack>
              </Box>
            </Paper>
          )}
        </>
      )}
    </Box>
  );
}
